export default {
    namespaced: true,
    state: {
        data: {},
        loadingPromises: {} // e.g. { "tableName.columnName": Promise }
    },
    mutations: {
        data(state, { tableName, columnName, data }) {
            // 1. If the table doesn't exist yet, initialize it cleanly
            if (!state.data[tableName]) {
                state.data = {
                    ...state.data,
                    [tableName]: { [columnName]: data }
                };
            } else {
                // 2. If the table DOES exist, we must break the reference of the inner object
                //    so Vue detects the brand-new column key.
                state.data = {
                    ...state.data,
                    [tableName]: {
                        ...state.data[tableName],
                        [columnName]: data
                    }
                };
            }
        },
        // Saves the pending promise into the tracking object
        setLoadingPromise(state, { cacheKey, promise }) {
            state.loadingPromises = {
                ...state.loadingPromises,
                [cacheKey]: promise
            };
        },
        // Removes the promise once it resolves or fails, cleaning up memory
        removeLoadingPromise(state, cacheKey) {
            const updatedPromises = { ...state.loadingPromises };
            delete updatedPromises[cacheKey];
            state.loadingPromises = updatedPromises;
        },
        itemData(state, {tableName, columnName, itemId, data}) {
            if (state.data[tableName] === undefined)
                this._vm.$set(state.data, tableName, {})
            
            if (state.data[tableName][columnName] === undefined)
                this._vm.$set(state.data[tableName], columnName, [])
            
            state.data[tableName][columnName] = state.data[tableName][columnName]
                // Remove the item's old category data
                .filter(o => o.itemId !== itemId)
                // Add the new data
                .concat(data)
        }
    },
    actions: {
        async refreshComponentData({rootState, rootGetters, dispatch}, {tableName}) {
            const componentId = rootState.components.componentIds[tableName]
            
            // 1. Get the filtered array of columns
            const relationships = rootGetters['componentStructure/get'](componentId)
                .filter(o => o.type === 'relationshipManyToMany')
            
            // 2. Map them to an array of dispatch promises and wait for all to resolve
            await Promise.all(
                relationships.map(({columnName}) =>
                    dispatch('getTableColumnData', {tableName, columnName, refresh: true})
                )
            )
        },
        async getTableColumnData({ state, commit, dispatch }, { tableName, columnName, refresh }) {
            const cacheKey = `${tableName}.${columnName}`;
            
            // 1. If we already have the data and aren't forcing a refresh, return it immediately
            if (state.data?.[tableName]?.[columnName] !== undefined && !refresh) {
                return state.data[tableName][columnName];
            }
            
            // 2. If a request for this specific data is already in progress, return that ongoing promise
            if (state.loadingPromises[cacheKey] && !refresh) {
                return state.loadingPromises[cacheKey];
            }
            
            // 3. Create the promise for the API call
            const requestPromise = (async () => {
                try {
                    const { data } = await dispatch('request/get', {
                        url: `api/categories/get-table-column-data/${tableName}/${columnName}`,
                    }, { root: true });
                    
                    commit('data', { tableName, columnName, data });
                    return data;
                } finally {
                    // Clean up the loading promise tracking once finished/failed
                    commit('removeLoadingPromise', cacheKey);
                }
            })();
            
            // Store the promise in the state so later components can await it
            commit('setLoadingPromise', { cacheKey, promise: requestPromise });
            
            return requestPromise;
        },
        async enableCategories({rootState, dispatch, rootGetters}, {componentId}) {
            const {tableName, title, roles, rolesGroup} = rootState.components.items.find(o => o.id === componentId)
            const categoryTitle = title + ' categories'
            const categoryTableName = tableName + '_categories'
            
            // Validate - The "<tableName>_categories" table cannot exist
            if (rootState.components.componentIds[categoryTableName] !== undefined) {
                dispatch('toasts/add', {
                    body: `
                        <p>The table <strong>${categoryTableName}</strong> exists!</p>
                        <p class="mb-0">Unable to create the category component.</p>
                    `,
                    colourScheme: 'danger',
                    disableAutoHide: true
                }, {root: true})
                return
            }
            
            // Validate - The "category" field cannot exist
            const fieldName = 'category'
            const fields = rootGetters['componentStructure/get'](componentId)
            if (fields.find(o => o.columnName === fieldName)) {
                dispatch('toasts/add', {
                    body: `
                        <p>The field <strong>${fieldName}</strong> exists!</p>
                        <p class="mb-0">Unable to create the category component.</p>
                    `,
                    colourScheme: 'danger',
                    disableAutoHide: true
                }, {root: true})
                return
            }
            
            // Get the fieldset ID for the new category field.
            // It goes at the bottom of the first fieldset.
            const {data: {values}} = (await dispatch('request/get', {
                url: 'api/component/fieldset/id',
                params: {componentId, sort: 'displayOrder'}
            }, {root: true}))
            const firstFieldsetId = values[0]
            
            // Create the new categories component
            const {data: {id: newComponentId}} = (await dispatch('itemData/insert', {
                tableName: 'modulecomponents',
                data: {
                    title: categoryTitle,
                    tableName: categoryTableName,
                    showDisplayOrder: 1,
                    addNew: 1,
                    updateOption: 1,
                    deleteOption: 1,
                    showMetadata: 1,
                    useArchive: 1,
                    enableCache: 1,
                    roles, // Inherits from component
                    rolesGroup // Inherits from component
                }
            }, {root: true}))
            
            // Create the new Category field
            const {data: {id: categoryFieldId}} = (await dispatch('itemData/insert', {
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: componentId, // todo Deprecated
                    modulecomponentsId: componentId,
                    fieldsetId: firstFieldsetId,
                    categoryComponentId: newComponentId,
                    title: 'Category',
                    columnName: 'category',
                    type: 'relationshipManyToMany'
                }
            }, {root: true}))
            
            // Update the component's settings with the new component and field's properties
            await dispatch('itemData/update', {
                tableName: 'modulecomponents',
                id: componentId,
                data: {
                    categoryColumn: categoryFieldId,
                    categoryComponent: newComponentId,
                    categoryList: 1
                }
            }, {root: true})
            
            // Create the new category component's first fieldset.
            const {data: {id: fieldsetId}} = (await dispatch('itemData/insert', {
                tableName: 'fieldset',
                data: {
                    componentId: newComponentId,
                    legend: 'General'
                }
            }, {root: true}))
            
            // Create the new category component's Title field.
            await dispatch('itemData/insert', {
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Title',
                    columnName: 'title',
                    type: 'text',
                    browser: 1
                }
            }, {root: true})
            
            // Create the new category component's Parent category field.
            await dispatch('itemData/insert', {
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Parent category',
                    columnName: 'parentId',
                    type: 'relationshipOneToMany',
                    categoryComponentId: newComponentId,
                    browser: 1
                }
            }, {root: true})
            
            // Create the new category component's Hide field.
            await dispatch('itemData/insert', {
                tableName: 'modulestructure',
                data: {
                    modulecomponentsID: newComponentId, // todo Deprecated
                    modulecomponentsId: newComponentId,
                    fieldsetId,
                    title: 'Hide',
                    columnName: 'hide',
                    type: 'checkbox',
                    browser: 1
                }
            }, {root: true})
            
            return newComponentId
        },
        async disableCategories({rootState, dispatch}, {componentId}) {
            const {categoryColumn, categoryComponent} = rootState.components.items.find(o => o.id === componentId)
            
            // Unset the category specific fields in the component.
            await dispatch('itemData/update', {
                tableName: 'modulecomponents',
                id: componentId,
                data: {
                    categoryColumn: 0,
                    categoryComponent: 0,
                    categoryList: 0
                }
            }, {root: true})
            
            // Delete the category column. E.g. Services -> categories
            if (categoryColumn) {
                await dispatch('itemData/delete', {
                    tableName: 'modulestructure',
                    id: categoryColumn
                }, {root: true})
                
                await dispatch('componentStructure/init', {}, {root: true})
            }
            
            // Delete the category component. E.g. Services categories
            await dispatch('itemData/delete', {
                tableName: 'modulecomponents',
                id: categoryComponent
            }, {root: true})
            
            await dispatch('components/init', {}, {root: true})
        }
    }
}